import { DatePickerField } from 'lib/iterable-hook-form';
import isEmpty from 'lodash/isEmpty';
import { StyledVariantFormField } from 'modules/experiments/components/pages/edit/sections/variant_configuration/CommonStyled';
import { useTakeoverExperimentContext, useVariantCardContext } from 'modules/experiments/contexts';
import { useExperimentsTranslations } from 'modules/experiments/hooks';
import { ComboTextFieldProps, VariantFormFieldKey } from 'modules/experiments/types';
import { formatSendTimeDateTimeStamp } from 'modules/experiments/utils';
import moment from 'moment';
import { useEffect } from 'react';
import { FieldError, useController, useFormContext } from 'react-hook-form';
import { TimeIn24HourClock } from 'ui/datePicker';
import { useSchemaValidationErrorTranslations } from 'utils/hooks/useTranslation';
import { ReadOnlyValue } from '../ReadOnlyValue';
import { DateTimePickerWrapper, StyledTypeaheadTimePickerField } from './Styled';

export const SendTime = ({ formFields, fieldValues, isFieldEditable }: ComboTextFieldProps) => {
  const {
    formState: { errors },
    getValues,
    setValue,
    trigger,
  } = useFormContext();
  const { t } = useExperimentsTranslations();
  const { zt } = useSchemaValidationErrorTranslations();
  const { cardIndex, variant } = useVariantCardContext();
  const {
    sendTimeMetaInfo: {
      sendTimeStartTimeZone,
      sendTimeStartTimeZoneAbbr,
      sendTimeUseRecipientTimeZones,
      scheduleDateRange,
    },
  } = useTakeoverExperimentContext();
  const isValid = isEmpty(errors);

  const [dateFormField, timeFormField] = formFields;
  const dateKey = dateFormField.key;
  const timeKey = timeFormField.key;

  // Collecting all the field error messages
  const dateFieldErrors = errors[dateKey];
  const timeFieldErrors = errors[timeKey];
  const groupFieldErrors = (
    dateFieldErrors as Record<VariantFormFieldKey, FieldError> | undefined
  )?.[timeKey];

  const errorMessage =
    (groupFieldErrors || dateFieldErrors || timeFieldErrors)?.message?.toString() || '';

  const {
    field: { onChange: onTimeChange, value: timePickerValue },
  } = useController({
    name: timeKey,
  });

  const {
    field: { onChange: onDateChange, value: datePickerValue },
  } = useController({
    name: dateKey,
  });

  const timeZoneValue = sendTimeUseRecipientTimeZones
    ? `(${t('placeholder.recipientAbbr')})`
    : `(${sendTimeStartTimeZoneAbbr})`;

  const timeZoneValueReadOnly = sendTimeUseRecipientTimeZones
    ? `(${t('placeholder.recipient')})`
    : `(${sendTimeStartTimeZoneAbbr})`;

  const label =
    cardIndex === 0
      ? t('variantCard.fieldLabel.startDateAndTime')
      : t('variantCard.fieldLabel.sendTime');

  const handleTimePickerChange = (time: TimeIn24HourClock) => {
    onTimeChange(time);

    /**
     * The goal here is to mimic the behavior of the DatePicker component’s internal
     * `handleSetDate` logic, which wraps a native JS Date in a `moment(...)` and
     * calls `onDateChange(...)` with that new value.
     *
     * When the user selects a time (but not a new date), the DatePicker’s internal
     * state isn’t re-triggered — so we re-invoke `onDateChange` manually with a
     * reconstructed Moment to simulate an identical date selection.
     *
     * This ensures that all validation logic and form sync behave as if the user
     * re-selected the date through the UI.
     */
    if (datePickerValue) {
      const jsDate = new Date(
        datePickerValue.year(),
        datePickerValue.month(),
        datePickerValue.date(),
      );
      const reconstructedMoment = moment(jsDate);

      onDateChange(reconstructedMoment);
    }
  };

  /**
   * Update the start time zone in the field's meta info
   * as the user updates the Start timezone in the experiment settings
   * And, run a validation after change to make sure the new start time
   * is still in the future
   */
  useEffect(() => {
    setValue(VariantFormFieldKey.SEND_TIME_VALIDATION_META_INFO, {
      ...getValues(VariantFormFieldKey.SEND_TIME_VALIDATION_META_INFO),
      experimentSendTimeZone: sendTimeStartTimeZone,
    });
    trigger();
  }, [sendTimeStartTimeZone]);

  /**
   * As group errors are tied to the date field
   * we need to manually trigger a validation
   * each time the time field changes
   */
  useEffect(() => {
    trigger();
  }, [timePickerValue]);

  return (
    <StyledVariantFormField
      labelText={label}
      errorText={zt(errorMessage)}
      hasError={!isValid}
      isHorizontalLayout
    >
      {isFieldEditable ? (
        <DateTimePickerWrapper>
          <DatePickerField name={dateFormField.key} validDateRange={scheduleDateRange} />
          <StyledTypeaheadTimePickerField
            isError={!isValid}
            increment={15}
            inputClockFormat="12-hour"
            time={timePickerValue}
            Suffix={timeZoneValue}
            onSelect={([time]) => handleTimePickerChange(time as TimeIn24HourClock)}
          />
        </DateTimePickerWrapper>
      ) : (
        <ReadOnlyValue dataTest={`variant-card-${variant.alphabet}-read-only-value`}>
          {formatSendTimeDateTimeStamp(
            fieldValues[dateFormField.key],
            fieldValues[timeFormField.key],
            timeZoneValueReadOnly,
          )}
        </ReadOnlyValue>
      )}
    </StyledVariantFormField>
  );
};
